import * as THREE from '../../libs/three.js/build/three.module.js';
import { Measure } from './Measure.js';
import { Utils } from '../utils.js';
import { CameraMode } from '../defines.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { Volume, SphereVolume } from '../utils/Volume.js';

const BIM_SNAP_KEYS = { p: 'point', l: 'line', s: 'surface' };

/** P/L/S during measurement. Returns true if the event was a snap-mode key. */
function applyBimSnapKey(viewer, e) {
  if (e.type !== 'keydown') return false;
  if (e.altKey || e.ctrlKey || e.metaKey) return false;
  const mode = BIM_SNAP_KEYS[(e.key || '').toLowerCase()];
  if (!mode) return false;
  // First-person uses S for backward; leave it alone when that control is active.
  if (mode === 'surface' && viewer.fpControls?.enabled) return false;
  viewer.measurementBimSnapMode = mode;
  document.querySelectorAll('input.measure-snap-mode').forEach((input) => {
    input.checked = input.value === mode;
  });
  if (typeof e.preventDefault === 'function') e.preventDefault();
  return true;
}

/** Seed / refresh the pending marker from the cursor (BIM snap is async without point cloud). */
function seedPendingMarker(viewer, measure) {
  if (measure.points.length === 0) return;
  const mouse = viewer.inputHandler?.mouse;
  if (!mouse) return;
  measure.syncMarkerFromMouse(viewer, measure.points.length - 1, mouse.clone());
}


function updateAzimuth(viewer, measure) {
  const azimuth = measure.azimuth;

  const isOkay = measure.points.length === 2;

  azimuth.node.visible = isOkay && measure.showAzimuth;

  if (!azimuth.node.visible) {
    return;
  }

  const camera = viewer.scene.getActiveCamera();
  const renderAreaSize = viewer.renderer.getSize(new THREE.Vector2());
  const width = renderAreaSize.width;
  const height = renderAreaSize.height;

  const [p0, p1] = measure.points;
  const r = p0.position.distanceTo(p1.position);
  const northVec = Utils.getNorthVec(p0.position, r, viewer.getProjection());
  const northPos = p0.position.clone().add(northVec);
  azimuth.center.position.copy(p0.position);
  azimuth.center.scale.set(2, 2, 2);

  azimuth.center.visible = false;
  // azimuth.target.visible = false;

  {
    // north
    azimuth.north.position.copy(northPos);

    let distance = azimuth.north.position.distanceTo(camera.position);
    let pr = Utils.projectedRadius(1, camera, distance, width, height);

    let scale = 5 / pr;
    azimuth.north.scale.set(scale, scale, scale);

    azimuth.northLabel.position.copy(northPos);
    let labelScale = 70 / pr;
    azimuth.northLabel.scale.set(labelScale, labelScale, labelScale);
  }

  {
    // target
    azimuth.target.position.copy(p1.position);
    azimuth.target.position.z = azimuth.north.position.z;

    let distance = azimuth.target.position.distanceTo(camera.position);
    let pr = Utils.projectedRadius(1, camera, distance, width, height);

    let scale = 5 / pr;
    azimuth.target.scale.set(scale, scale, scale);
  }

  azimuth.circle.position.copy(p0.position);
  azimuth.circle.scale.set(r, r, r);
  azimuth.circle.material.resolution.set(width, height);

  // to target
  azimuth.centerToTarget.geometry.setPositions([
    0,
    0,
    0,
    ...p1.position.clone().sub(p0.position).toArray(),
  ]);
  azimuth.centerToTarget.position.copy(p0.position);
  azimuth.centerToTarget.geometry.verticesNeedUpdate = true;
  azimuth.centerToTarget.geometry.computeBoundingSphere();
  azimuth.centerToTarget.computeLineDistances();
  azimuth.centerToTarget.material.resolution.set(width, height);

  // to target ground
  azimuth.centerToTargetground.geometry.setPositions([
    0,
    0,
    0,
    p1.position.x - p0.position.x,
    p1.position.y - p0.position.y,
    0,
  ]);
  azimuth.centerToTargetground.position.copy(p0.position);
  azimuth.centerToTargetground.geometry.verticesNeedUpdate = true;
  azimuth.centerToTargetground.geometry.computeBoundingSphere();
  azimuth.centerToTargetground.computeLineDistances();
  azimuth.centerToTargetground.material.resolution.set(width, height);

  // to north
  azimuth.centerToNorth.geometry.setPositions([
    0,
    0,
    0,
    northPos.x - p0.position.x,
    northPos.y - p0.position.y,
    0,
  ]);
  azimuth.centerToNorth.position.copy(p0.position);
  azimuth.centerToNorth.geometry.verticesNeedUpdate = true;
  azimuth.centerToNorth.geometry.computeBoundingSphere();
  azimuth.centerToNorth.computeLineDistances();
  azimuth.centerToNorth.material.resolution.set(width, height);

  // label
  const radians = Utils.computeAzimuth(
    p0.position,
    p1.position,
    viewer.getProjection()
  );
  let degrees = THREE.MathUtils.radToDeg(radians);
  if (degrees < 0) {
    degrees = 360 + degrees;
  }
  const txtDegrees = `${degrees.toFixed(2)}°`;
  const labelDir = northPos
    .clone()
    .add(p1.position)
    .multiplyScalar(0.5)
    .sub(p0.position);
  if (labelDir.length() > 0) {
    labelDir.z = 0;
    labelDir.normalize();
    const labelVec = labelDir.clone().multiplyScalar(r);
    const labelPos = p0.position.clone().add(labelVec);
    azimuth.label.position.copy(labelPos);
  }
  azimuth.label.setText(txtDegrees);
  let distance = azimuth.label.position.distanceTo(camera.position);
  let pr = Utils.projectedRadius(1, camera, distance, width, height);
  let scale = 70 / pr;
  azimuth.label.scale.set(scale, scale, scale);
}

export class MeasuringTool extends EventDispatcher {
  constructor(viewer) {
    super();
    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.pause = false;
    this.addEventListener('start_inserting_measurement', (e) => {
      this.viewer.dispatchEvent({
        type: 'cancel_insertions',
      });
    });

    this.showLabels = true;
    this.scene = new THREE.Scene();
    this.scene.name = 'scene_measurement';
    this.light = new THREE.DirectionalLight(0xffffff);
    this.ambientLight = new THREE.AmbientLight(0x222222);
    this.lightTargetObject = new THREE.Object3D();
    this.scene.add(this.light);
    this.scene.add(this.ambientLight);
    this.scene.add(this.lightTargetObject);
    this.light.target = this.lightTargetObject;
    this.currentMeasurement = null;
    this.pauseCanToggle = false;
    // Handle keyboard events for axis locking
    // Listen on the renderer domElement which gets focus, and also on document as fallback
    const handleKeyDown = (e) => {
      if (e.code.toString().localeCompare("ControlLeft") === 0) {
        this.pauseCanToggle = true;
      }
      
    };
    
    const handleKeyUp = (e) => {
      if (e.code.toString().localeCompare("ControlLeft") === 0 && this.currentMeasurement !== null && this.pauseCanToggle) {
        this.continueInsertion(this.currentMeasurement);
        this.currentMeasurement = null;
        this.pauseCanToggle = false;
      }
    };
    
    // Attach to both renderer domElement (which gets focus) and document (as fallback)
    // Use capture phase to catch events before InputHandler processes them
    this.viewer.renderer.domElement.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    this.viewer.renderArea.addEventListener('keydown', handleKeyDown, true);
    
    this.viewer.renderer.domElement.addEventListener('keyup', handleKeyUp, true);
    this.viewer.renderArea.addEventListener('keyup', handleKeyUp, true);
    
    // Store handlers for cleanup if needed
    this._axisLockKeyDownHandler = handleKeyDown;
    this._axisLockKeyUpHandler = handleKeyUp;

    this.viewer.inputHandler.registerInteractiveScene(this.scene);

    this.onRemove = (e) => {
      this.scene.remove(e.measurement);
    };
    this.onAdd = (e) => {
      this.scene.add(e.measurement);
    };

    for (let measurement of viewer.scene.measurements) {
      this.onAdd({ measurement: measurement });
    }

    viewer.addEventListener('update', this.update.bind(this));
    viewer.addEventListener(
      'render.pass.perspective_overlay',
      this.render.bind(this)
    );
    viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

    viewer.scene.addEventListener('measurement_added', this.onAdd);
    viewer.scene.addEventListener('measurement_removed', this.onRemove);
    this.onSurfaceAdd = (e) => {
      this.scene.add(e.surface.overlay);
    };
    this.onSurfaceRemove = (e) => {
      this.scene.remove(e.surface.overlay);
      e.surface.dispose?.();
    };
    viewer.scene.addEventListener('surface_added', this.onSurfaceAdd);
    viewer.scene.addEventListener('surface_removed', this.onSurfaceRemove);
    viewer.scene.addEventListener('measurement_removed', this.onRemove);
  }

  onSceneChange(e) {
    if (e.oldScene) {
      e.oldScene.removeEventListener('measurement_added', this.onAdd);
      e.oldScene.removeEventListener('measurement_removed', this.onRemove);
      e.oldScene.removeEventListener('surface_added', this.onSurfaceAdd);
      e.oldScene.removeEventListener('surface_removed', this.onSurfaceRemove);
    }

    e.scene.addEventListener('measurement_added', this.onAdd);
    e.scene.addEventListener('measurement_removed', this.onRemove);
    e.scene.addEventListener('surface_added', this.onSurfaceAdd);
    e.scene.addEventListener('surface_removed', this.onSurfaceRemove);
  }

  continueInsertion(measure) {
    //nie edytować punktu, azymutu, objętości, wysokość, okrąg
    if (measure.maxMarkers !== Infinity || measure instanceof Volume) {
      alert('Cannot add points to this measurement');
      return;
    }

    let domElement = this.viewer.renderer.domElement;
    domElement.focus();
    
    // Set current measurement for axis lock keyboard shortcuts
    this.currentMeasurement = measure;

    this.dispatchEvent({
      type: 'start_inserting_measurement',
      measure: measure,
    });

    // Detect mobile device
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (typeof window.orientation !== 'undefined') ||
             (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
             ('ontouchstart' in window && navigator.maxTouchPoints > 1);
    };
    
    const isMobileDevice = isMobile();

    let cancel = {
      // On mobile, don't remove last marker - each tap adds a final marker, no pending marker to remove
      // On desktop, remove last marker when maxMarkers > 3 (there's a pending dragged marker)
      removeLastMarker: !isMobileDevice && measure.maxMarkers > 3,
      callback: null,
    };

    // Track last processed time for debouncing touch + emulated mouse events
    let lastProcessedTime = 0;
    const DEBOUNCE_MS = 100;

    // Helper to get mouse position from event (works for both mouse and touch)
    const getMousePosition = (e) => {
      let rect = domElement.getBoundingClientRect();
      let x, y;
      
      if (e.changedTouches && e.changedTouches.length > 0) {
        // Touch event
        x = e.changedTouches[0].pageX - rect.left;
        y = e.changedTouches[0].pageY - rect.top;
      } else {
        // Mouse event
        x = (e.clientX || e.pageX) - rect.left;
        y = (e.clientY || e.pageY) - rect.top;
      }
      
      return new THREE.Vector2(x, y);
    };

    let insertionCallback = (e) => {
      // Do not swallow BIM snap shortcuts (P/L/S) — they must reach the overlay.
      if (applyBimSnapKey(this.viewer, e)) {
        return;
      }

      // Debounce to prevent double processing of touch + emulated mouse events
      const now = Date.now();
      if (now - lastProcessedTime < DEBOUNCE_MS) {
        return;
      }
      
      // Zatrzymaj propagację eventu
      if (e.stopPropagation) {
        e.stopPropagation();
      }
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      if (e.button === THREE.MOUSE.MIDDLE) {
        return;
      }

      // Handle touchend events for mobile
      if (e.type === 'touchend' && e.changedTouches && e.changedTouches.length === 1) {
        lastProcessedTime = now;
        
        let mouse = getMousePosition(e);
        let I = Utils.getMousePointCloudIntersection(
          mouse,
          this.viewer.scene.getActiveCamera(),
          this.viewer,
          this.viewer.scene.pointclouds,
          { pickClipped: true }
        );
        
        let markerPosition = I ? I.location : measure.points[measure.points.length - 1].position.clone();
        
        measure.addMarker(
          I
            ? { position: markerPosition, snapSource: I.source }
            : markerPosition
        );
        measure.sphereDragged = measure.points[measure.points.length - 1];

        if (measure.points.length >= measure.maxMarkers) {
          cancel.callback();
        }
        return;
      }

      if (e.button === THREE.MOUSE.LEFT) {
        lastProcessedTime = now;
        void (async () => {
          if (!isMobileDevice && measure.points.length > 0) {
            const mouse =
              this.viewer.inputHandler?.mouse?.clone() ?? getMousePosition(e);
            await measure.ensureMarkerSnappedFromMouse(
              this.viewer,
              measure.points.length - 1,
              mouse
            );
          }

          // For single-point tools (e.g. Point), we already have the point from initial add + drag; don't add again on release
          if (measure.points.length >= measure.maxMarkers) {
            cancel.callback();
            return;
          }

          let markerPosition;
          let snapSource;

          if (isMobileDevice) {
            // Mobile: use click/tap position
            let mouse = getMousePosition(e);
            let I = Utils.getMousePointCloudIntersection(
              mouse,
              this.viewer.scene.getActiveCamera(),
              this.viewer,
              this.viewer.scene.pointclouds,
              { pickClipped: true }
            );
            markerPosition = I
              ? I.location
              : measure.points[measure.points.length - 1].position.clone();
            snapSource = I?.source;
          } else {
            // Desktop: use previous point position (original behavior)
            markerPosition = measure.points[measure.points.length - 1].position.clone();
            snapSource = measure.points[measure.points.length - 1].snapSource;
          }

          measure.addMarker(
            snapSource
              ? { position: markerPosition, snapSource }
              : markerPosition
          );
          measure.sphereDragged = measure.points[measure.points.length - 1];

          if (measure.points.length >= measure.maxMarkers) {
            cancel.callback();
          }

          this.viewer.inputHandler.startDragging(
            measure.spheres[measure.spheres.length - 1]
          );
          seedPendingMarker(this.viewer, measure);
        })();
      } else if (e.button === THREE.MOUSE.RIGHT || e.key === 'Escape') {
        this.currentMeasurement = null;
        cancel.callback();
      }
      else if (e.type.toString().localeCompare('keyup') === 0 && e.code
        .toString().localeCompare('ControlLeft') === 0 && this.currentMeasurement === null) {
        this.currentMeasurement = measure;
        this.pauseCanToggle = false;
        cancel.callback();
      }
    };

    cancel.callback = (e) => {
      if (cancel.removeLastMarker) {
        measure.removeMarker(measure.points.length - 1);
      }
      measure.sphereDragged = undefined;
      this.viewer.inputHandler.startDragging(null);
      domElement.removeEventListener('mouseup', insertionCallback, false);
      domElement.removeEventListener('touchend', insertionCallback, false);
      domElement.removeEventListener('keydown', insertionCallback, false);
      domElement.removeEventListener('keyup', insertionCallback, false);
      document.removeEventListener('keydown', insertionCallback, true);
      this.viewer.removeEventListener('cancel_insertions', cancel.callback);
      this.viewer.dispatchEvent({ type: 'cancel_insertions' });

      // If measurement is complete (reached maxMarkers), disable measure mode to return to navigation
      if (measure.maxMarkers !== Infinity && measure.points.length >= measure.maxMarkers) {
        this.viewer.measureModeEnabled = false;
        this.viewer.inputHandler.measureModeEnabled = false;

        // Dispatch event so mobile toolbar can update its state
        this.viewer.dispatchEvent({
          type: 'measurement_completed',
          measurement: measure
        });
      }
    };

    // Always add event listeners (including for single-marker measurements)
    this.viewer.addEventListener('cancel_insertions', cancel.callback);
    domElement.addEventListener('keydown', insertionCallback, false);
    document.addEventListener('keydown', insertionCallback, true); // capture: works even without canvas focus
    domElement.addEventListener('keyup', insertionCallback, false);
    domElement.addEventListener('mouseup', insertionCallback, false);
    domElement.addEventListener('touchend', insertionCallback, false);

    // Desktop: Add marker automatically and start dragging (original behavior)
    // Mobile: Wait for next tap - don't add marker automatically
    if (!isMobileDevice) {
      measure.addMarker(new THREE.Vector3(0, 0, 0));
      this.viewer.inputHandler.startDragging(
        measure.spheres[measure.spheres.length - 1]
      );
      seedPendingMarker(this.viewer, measure);
    }

    return measure;
  }

  startInsertion(args = {}) {
    let domElement = this.viewer.renderer.domElement;
    
    // Detect mobile device
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (typeof window.orientation !== 'undefined') ||
             (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
             ('ontouchstart' in window && navigator.maxTouchPoints > 1);
    };
    
    const isMobileDevice = isMobile();

    let measure = new Measure();
    measure.displayCoordOffset = this.viewer.measurementDisplayOffset ?? null;

    this.dispatchEvent({
      type: 'start_inserting_measurement',
      measure: measure,
    });

    const pick = (defaul, alternative) => {
      if (defaul != null) {
        return defaul;
      } else {
        return alternative;
      }
    };

    measure.showDistances =
      args.showDistances === null ? true : args.showDistances;

    measure.showArea = pick(args.showArea, false);
    measure.showTotalDistance = pick(args.showTotalDistance, false);
    measure.showAngles = pick(args.showAngles, false);
    measure.showCoordinates = pick(args.showCoordinates, false);
    measure.showHeight = pick(args.showHeight, false);
    measure.showCircle = pick(args.showCircle, false);
    measure.showAzimuth = pick(args.showAzimuth, false);
    measure.showEdges = pick(args.showEdges, true);
    measure.closed = pick(args.closed, false);
    measure.maxMarkers = pick(args.maxMarkers, Infinity);

    measure.name = args.name || 'Measurement';

    this.scene.add(measure);

    let cancel = {
      // On mobile, don't remove last marker - each tap adds a final marker, no pending marker to remove
      // On desktop, remove last marker when maxMarkers > 3 (there's a pending dragged marker)
      removeLastMarker: !isMobileDevice && measure.maxMarkers > 3,
      callback: null,
    };

    // Track last processed time for debouncing touch + emulated mouse events
    let lastProcessedTime = 0;
    const DEBOUNCE_MS = 100;

    // Helper to get mouse position from event (works for both mouse and touch)
    const getMousePosition = (e) => {
      let rect = domElement.getBoundingClientRect();
      let x, y;
      
      if (e.changedTouches && e.changedTouches.length > 0) {
        // Touch event
        x = e.changedTouches[0].pageX - rect.left;
        y = e.changedTouches[0].pageY - rect.top;
      } else {
        // Mouse event
        x = (e.clientX || e.pageX) - rect.left;
        y = (e.clientY || e.pageY) - rect.top;
      }
      
      return new THREE.Vector2(x, y);
    };

    let insertionCallback = (e) => {
      // Do not swallow BIM snap shortcuts (P/L/S) — they must reach the overlay.
      if (applyBimSnapKey(this.viewer, e)) {
        return;
      }

      // Debounce to prevent double processing of touch + emulated mouse events
      const now = Date.now();
      if (now - lastProcessedTime < DEBOUNCE_MS) {
        return;
      }
      
      // Zatrzymaj propagację eventu
      if (e.stopPropagation) {
        e.stopPropagation();
      }
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      if (e.button === THREE.MOUSE.MIDDLE) {
        return;
      }

      // Handle touchend events for mobile
      if (e.type === 'touchend' && e.changedTouches && e.changedTouches.length === 1) {
        lastProcessedTime = now;
        
        let mouse = getMousePosition(e);
        let I = Utils.getMousePointCloudIntersection(
          mouse,
          this.viewer.scene.getActiveCamera(),
          this.viewer,
          this.viewer.scene.pointclouds,
          { pickClipped: true }
        );
        
        let markerPosition = I ? I.location : (measure.points.length > 0 ? measure.points[measure.points.length - 1].position.clone() : new THREE.Vector3(0, 0, 0));
        
        measure.addMarker(
          I
            ? { position: markerPosition, snapSource: I.source }
            : markerPosition
        );
        measure.sphereDragged = measure.points[measure.points.length - 1];

        if (measure.points.length >= measure.maxMarkers) {
          cancel.callback(null, true);
        }
        return;
      }

      if (e.button === THREE.MOUSE.LEFT) {
        lastProcessedTime = now;
        void (async () => {
          if (!isMobileDevice && measure.points.length > 0) {
            const mouse =
              this.viewer.inputHandler?.mouse?.clone() ?? getMousePosition(e);
            await measure.ensureMarkerSnappedFromMouse(
              this.viewer,
              measure.points.length - 1,
              mouse
            );
          }

          // For single-point tools (e.g. Point), we already have the point from initial add + drag; don't add again on release
          if (measure.points.length >= measure.maxMarkers) {
            cancel.callback(null, true);
            return;
          }
          let markerPosition;
          let snapSource;

          if (isMobileDevice && measure.points.length === 0) {
            // Mobile: first marker at click/tap position
            let mouse = getMousePosition(e);
            let I = Utils.getMousePointCloudIntersection(
              mouse,
              this.viewer.scene.getActiveCamera(),
              this.viewer,
              this.viewer.scene.pointclouds,
              { pickClipped: true }
            );
            markerPosition = I ? I.location : new THREE.Vector3(0, 0, 0);
            snapSource = I?.source;
          } else if (isMobileDevice && measure.points.length > 0) {
            // Mobile: subsequent markers at click/tap position
            let mouse = getMousePosition(e);
            let I = Utils.getMousePointCloudIntersection(
              mouse,
              this.viewer.scene.getActiveCamera(),
              this.viewer,
              this.viewer.scene.pointclouds,
              { pickClipped: true }
            );
            markerPosition = I
              ? I.location
              : measure.points[measure.points.length - 1].position.clone();
            snapSource = I?.source;
          } else {
            // Desktop: use previous point position (original behavior from main)
            markerPosition = measure.points[measure.points.length - 1].position.clone();
            snapSource = measure.points[measure.points.length - 1].snapSource;
          }

          measure.addMarker(
            snapSource
              ? { position: markerPosition, snapSource }
              : markerPosition
          );
          measure.sphereDragged = measure.points[measure.points.length - 1];

          // Desktop: do NOT fire cancel here even if maxMarkers reached.
          // The user still needs one more click to confirm the last dragged marker.
          // Completion is triggered by the early-return above on the next click.

          this.viewer.inputHandler.startDragging(
            measure.spheres[measure.spheres.length - 1]
          );
          seedPendingMarker(this.viewer, measure);
        })();
      } else if (e.button === THREE.MOUSE.RIGHT || e.key === 'Escape') {
        this.currentMeasurement = null;
        cancel.callback();
      }
      else if (e.type.toString().localeCompare('keyup') === 0 && e.code
        .toString().localeCompare('ControlLeft') === 0 && this.currentMeasurement === null) {
        this.currentMeasurement = measure;
        this.pauseCanToggle = false;
        cancel.callback();
      }
    };

    cancel.callback = (e, completed = false) => {
      if (cancel.removeLastMarker) {
        measure.removeMarker(measure.points.length - 1);
      }
      measure.sphereDragged = undefined;
      this.viewer.inputHandler.startDragging(null);
      domElement.removeEventListener('mouseup', insertionCallback, false);
      domElement.removeEventListener('touchend', insertionCallback, false);
      domElement.removeEventListener('keydown', insertionCallback, false);
      domElement.removeEventListener('keyup', insertionCallback, false);
      document.removeEventListener('keydown', insertionCallback, true);
      this.viewer.removeEventListener('cancel_insertions', cancel.callback);
      this.viewer.dispatchEvent({ type: 'cancel_insertions' });

      if (completed && measure.maxMarkers !== Infinity) {
        // Fixed-marker measurement completed by user action — disable measure mode
        this.viewer.measureModeEnabled = false;
        this.viewer.inputHandler.measureModeEnabled = false;
        this.viewer.dispatchEvent({
          type: 'measurement_completed',
          measurement: measure
        });
      } else if (!completed && measure.maxMarkers !== Infinity) {
        // Cancelled before completing — remove incomplete measurement from scene
        this.viewer.scene.removeMeasurement(measure);
      } else if (measure.maxMarkers === Infinity) {
        // Infinite-marker tool (distance, area, etc.) — keep if points were placed, remove if empty
        if (measure.points.length > 0) {
          this.viewer.dispatchEvent({
            type: 'measurement_completed',
            measurement: measure
          });
        } else {
          this.viewer.scene.removeMeasurement(measure);
        }
      }
    };

    // Always add event listeners for measurements (including Point with maxMarkers=1)
    this.viewer.addEventListener('cancel_insertions', cancel.callback);
    domElement.addEventListener('keyup', insertionCallback, false);
    domElement.addEventListener('keydown', insertionCallback, false);
    document.addEventListener('keydown', insertionCallback, true); // capture: works even without canvas focus
    domElement.addEventListener('mouseup', insertionCallback, false);
    domElement.addEventListener('touchend', insertionCallback, false);

    // Desktop: Add first marker automatically and start dragging (original behavior)
    // Mobile: Wait for first tap - don't add marker automatically
    if (!isMobileDevice) {
      measure.addMarker(new THREE.Vector3(0, 0, 0));
      this.viewer.inputHandler.startDragging(
        measure.spheres[measure.spheres.length - 1]
      );
      seedPendingMarker(this.viewer, measure);
    }

    // Add measurement to scene immediately (original behavior from main)
    this.viewer.scene.addMeasurement(measure);
    
    // Set current measurement for axis lock keyboard shortcuts
    this.currentMeasurement = measure;
    
    return measure;
  }

  update() {
    let camera = this.viewer.scene.getActiveCamera();
    let domElement = this.renderer.domElement;
    let measurements = this.viewer.scene.measurements;

    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
    let clientWidth = renderAreaSize.width;
    let clientHeight = renderAreaSize.height;

    this.light.position.copy(camera.position);
    this.lightTargetObject.position.copy(camera.position).add(camera.getWorldDirection(new THREE.Vector3()));

    // make size independant of distance
    for (let measure of measurements) {
      measure.lengthUnit = this.viewer.lengthUnit;
      measure.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
      measure.displayCoordOffset = this.viewer.measurementDisplayOffset ?? null;
      measure.update();

      updateAzimuth(this.viewer, measure);

      // spheres
      //CODE BELOW MAKES APP CRASH ON SINGLE POINT MEASURE BEING CANCELED
      // COMMENTED UNTIL IT'S 100% SURE THIS DOESN'T CAUSE FURTHER PROBLEMS
      /*if (measure.spheres.length === 0 && this.currentMeasurement === null) {
        this.viewer.scene.dispatchEvent({
          type: 'measurement_removed',
          measurement: measure,
        });
      }*/
      for (let sphere of measure.spheres) {
        let distance = camera.position.distanceTo(
          sphere.getWorldPosition(new THREE.Vector3())
        );
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );
        let scale = 15 / pr;
        sphere.scale.set(scale, scale, scale);
      }

      // labels
      let labels = measure.edgeLabels.concat(measure.angleLabels);
      for (let label of labels) {
        let distance = camera.position.distanceTo(
          label.getWorldPosition(new THREE.Vector3())
        );
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );
        let scale = 70 / pr;

        if (Potree.debug.scale) {
          scale = Potree.debug.scale / pr;
        }

        label.scale.set(scale, scale, scale);
      }

      // coordinate labels
      for (let j = 0; j < measure.coordinateLabels.length; j++) {
        let label = measure.coordinateLabels[j];
        let sphere = measure.spheres[j];

        let distance = camera.position.distanceTo(
          sphere.getWorldPosition(new THREE.Vector3())
        );

        let screenPos = sphere
          .getWorldPosition(new THREE.Vector3())
          .clone()
          .project(camera);
        screenPos.x = Math.round(((screenPos.x + 1) * clientWidth) / 2);
        screenPos.y = Math.round(((-screenPos.y + 1) * clientHeight) / 2);
        screenPos.z = 0;
        screenPos.y -= 30;

        let labelPos = new THREE.Vector3(
          (screenPos.x / clientWidth) * 2 - 1,
          -(screenPos.y / clientHeight) * 2 + 1,
          0.5
        );
        labelPos.unproject(camera);
        if (this.viewer.scene.cameraMode == CameraMode.PERSPECTIVE) {
          let direction = labelPos.sub(camera.position).normalize();
          labelPos = new THREE.Vector3().addVectors(
            camera.position,
            direction.multiplyScalar(distance)
          );
        }
        label.position.copy(labelPos);
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );
        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      // height label
      if (measure.showHeight) {
        let label = measure.heightLabel;
        let baseLabel = measure.baseLabel;
        {
          let distance = label.position.distanceTo(camera.position);
          let baseDistance = baseLabel.position.distanceTo(camera.position);
          let pr = Utils.projectedRadius(
            1,
            camera,
            distance,
            clientWidth,
            clientHeight
          );
          let pr2 = Utils.projectedRadius(
            1,
            camera,
            baseDistance,
            clientWidth,
            clientHeight
          );
          let scale = 70 / pr;
          let scale2 = 70 / pr2;
          label.scale.set(scale, scale, scale);
          baseLabel.scale.set(scale2, scale2, scale2);
        }

        {
          // height edge
          let edge = measure.heightEdge;
          
          // Skip if measurement has no points yet
          if (measure.points.length === 0) {
            edge.visible = false;
          } else {
            let sorted = measure.points
              .slice()
              .sort((a, b) => a.position.z - b.position.z);
            let lowPoint = sorted[0].position.clone();
            let highPoint = sorted[sorted.length - 1].position.clone();
            let min = lowPoint.z;
            let max = highPoint.z;

          let start = new THREE.Vector3(highPoint.x, highPoint.y, min);
          let end = new THREE.Vector3(highPoint.x, highPoint.y, max);

          let lowScreen = lowPoint.clone().project(camera);
          let startScreen = start.clone().project(camera);
          let endScreen = end.clone().project(camera);

          let toPixelCoordinates = (v) => {
            let r = v.clone().addScalar(1).divideScalar(2);
            r.x = r.x * clientWidth;
            r.y = r.y * clientHeight;
            r.z = 0;

            return r;
          };

          let lowEL = toPixelCoordinates(lowScreen);
          let startEL = toPixelCoordinates(startScreen);
          let endEL = toPixelCoordinates(endScreen);

          let lToS = lowEL.distanceTo(startEL);
          let sToE = startEL.distanceTo(endEL);

          edge.geometry.lineDistances = [0, lToS, lToS, lToS + sToE];
          edge.geometry.lineDistancesNeedUpdate = true;

          edge.material.dashSize = 10;
          edge.material.gapSize = 10;
          }
        }
      }

      {
        // area label
        let label = measure.areaLabel;
        let distance = label.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );
        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      {
        // total distance label
        let label = measure.totalDistanceLabel;
        let distance = label.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );
        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      {
        // radius label
        let label = measure.circleRadiusLabel;
        let distance = label.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(
          1,
          camera,
          distance,
          clientWidth,
          clientHeight
        );

        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      {
        // edges
        const materials = [
          measure.circleRadiusLine.material,
          ...measure.edges.map((e) => e.material),
          measure.heightEdge.material,
          measure.circleLine.material,
        ];

        for (const material of materials) {
          material.resolution.set(clientWidth, clientHeight);
        }
      }

      if (!this.showLabels) {
        const labels = [
          ...measure.sphereLabels,
          ...measure.edgeLabels,
          ...measure.angleLabels,
          ...measure.coordinateLabels,
          measure.heightLabel,
          measure.baseLabel,
          measure.areaLabel,
          measure.totalDistanceLabel,
          measure.circleRadiusLabel,
        ];

        for (const label of labels) {
          label.visible = false;
        }
      }
    }
  }

  render() {
    // PotreeRenderer leaves WebGL BLEND disabled; reset so Three.js overlay
    // materials can re-enable blending when needed.
    this.viewer.renderer.state.reset();
    this.viewer.renderer.render(
      this.scene,
      this.viewer.scene.getActiveCamera()
    );
  }
}
